import { useMemo } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Button,
  Flex,
  Group,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { IconDownload, IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  createMRTColumnHelper,
  MantineReactTable,
  MRT_ColumnFiltersState,
  MRT_VisibilityState,
  useMantineReactTable,
} from "mantine-react-table";

import { useGetBookings } from "@/shared/api/generated/booking";
import { useGetCategoriesOptionallyfilteredbylocation } from "@/shared/api/generated/categories";
import { useGetCustomers } from "@/shared/api/generated/customers";
import { useGetDepartmentsOptionallyfilteredbylocation } from "@/shared/api/generated/departments";
import { GetBookingsItem } from "@/shared/api/generated/schemas";
import { AnchorLink } from "@/shared/components/AnchorLink";
import { MaskedDateInput } from "@/shared/components/MaskedDateInput";
import { getDefaultMRTOptions } from "@/shared/components/table/defaultMRTOptions";
import { countryListnbNO } from "@/shared/utils/countryList";
import { langugeListnbNO } from "@/shared/utils/languageList";

import { bookingPaymentFormLabels } from "../bookings/utils/payment-form";
import { bookingPaymentFormDrinksLabels } from "../bookings/utils/payment-form-drinks";
import { bookingPaymentStatusLabels } from "../bookings/utils/payment-status";
import { bookingStatusLabels } from "../bookings/utils/status";
import { bookingVisitCategoryLabels } from "../bookings/utils/visit-category";
import { downloadReportAsExcelFile } from "./export-report-to-excel";

type TimePeriod = "today" | "thisWeek" | "thisMonth" | "custom";

interface ReportSettingsProps {
  museumId?: string;
}

export default function ReportSettings({ museumId }: ReportSettingsProps) {
  const { t, i18n } = useLingui();

  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<MRT_VisibilityState>({
      key: "table:report-settings:column-visibility",
      defaultValue: {},
    });
  // Helper functions for date ranges
  const getTodayRange = (): [Date, Date] => [
    dayjs().startOf("day").toDate(),
    dayjs().endOf("day").toDate(),
  ];

  const getThisWeekRange = (): [Date, Date] => [
    dayjs().startOf("week").toDate(),
    dayjs().endOf("week").toDate(),
  ];

  const getThisMonthRange = (): [Date, Date] => [
    dayjs().startOf("month").toDate(),
    dayjs().endOf("month").toDate(),
  ];

  const [start, end] = getTodayRange();

  const [columnFilters, setColumnFilters] =
    useLocalStorage<MRT_ColumnFiltersState>({
      key: "table:report-settings:column-filters",
      getInitialValueInEffect: false,
      defaultValue: [{ id: "arrival", value: [start, end] }],
      // Serialize and deserialize dates properly
      serialize: (value) => {
        return JSON.stringify(
          value.map((filter) => {
            if (filter.id === "arrival" && Array.isArray(filter.value)) {
              return {
                ...filter,
                value: (filter.value as (Date | undefined)[]).map((date) =>
                  date instanceof Date ? date.toISOString() : date,
                ),
              };
            }
            return filter;
          }),
        );
      },
      deserialize: (value) => {
        if (!value) return [{ id: "arrival", value: [start, end] }];
        const parsed = JSON.parse(value) as MRT_ColumnFiltersState;
        return parsed.map((filter) => {
          if (filter.id === "arrival" && Array.isArray(filter.value)) {
            return {
              ...filter,
              value: (filter.value as (string | undefined)[]).map((date) =>
                typeof date === "string" && date ? new Date(date) : undefined,
              ),
            };
          }
          return filter;
        });
      },
    });

  // Derive time period from arrival filter
  const getTimePeriodFromFilter = (): TimePeriod => {
    const arrivalFilter = table.getColumn("arrival")?.getFilterValue() as [
      Date | null,
      Date | null,
    ];
    if (!arrivalFilter) return "today";

    const [rawStart, rawEnd] = arrivalFilter;
    if (!rawStart || !rawEnd) return "custom";

    // Ensure dates are Date objects
    const start = rawStart instanceof Date ? rawStart : new Date(rawStart);
    const end = rawEnd instanceof Date ? rawEnd : new Date(rawEnd);

    const [todayStart, todayEnd] = getTodayRange();
    if (
      dayjs(start).isSame(todayStart, "day") &&
      dayjs(end).isSame(todayEnd, "day")
    ) {
      return "today";
    }

    const [weekStart, weekEnd] = getThisWeekRange();
    if (
      dayjs(start).isSame(weekStart, "day") &&
      dayjs(end).isSame(weekEnd, "day")
    ) {
      return "thisWeek";
    }

    const [monthStart, monthEnd] = getThisMonthRange();
    if (
      dayjs(start).isSame(monthStart, "day") &&
      dayjs(end).isSame(monthEnd, "day")
    ) {
      return "thisMonth";
    }

    return "custom";
  };

  const handleTimePeriodChange = (value: TimePeriod) => {
    let dateRange: [Date | null, Date | null];

    switch (value) {
      case "today":
        dateRange = getTodayRange();
        break;
      case "thisWeek":
        dateRange = getThisWeekRange();
        break;
      case "thisMonth":
        dateRange = getThisMonthRange();
        break;
      case "custom":
        dateRange = [null, null];
        break;
    }

    table.getColumn("arrival")?.setFilterValue(dateRange);
  };

  const { data: customers } = useGetCustomers();
  const { data: departments } = useGetDepartmentsOptionallyfilteredbylocation({
    location: museumId,
  });
  const { data: categories } = useGetCategoriesOptionallyfilteredbylocation({
    location: museumId,
  });

  const { data: bookingsData, isLoading } = useGetBookings({
    includeProgram: true,
    includeCancelled: false,
    locations: museumId ? [museumId] : undefined,
  });

  const departmentOptions =
    departments?.data?.map((department) => department.name) ?? [];
  const customerOptions =
    customers?.data?.map((customer) => customer.name) ?? [];

  const categoryOptions = [
    ...(categories?.data?.map((category) => ({
      value: category.id.toString(),
      label: category.name,
    })) ?? []),
    { value: "__NO_CATEGORY__", label: t`Ingen kategori` },
  ];

  const categoryIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    categories?.data?.forEach((c) => {
      map[c.id.toString()] = c.name;
    });
    return map;
  }, [categories?.data]);

  const countryMap = useMemo(
    () => new Map(countryListnbNO.map((c) => [c.value, c.label])),
    [],
  );

  const languageMap = useMemo(
    () => new Map(langugeListnbNO.map((l) => [l.value, l.label])),
    [],
  );

  const handleClearFilters = () => {
    table.resetColumnFilters();
    table.resetColumnVisibility();
  };

  const handleGenerateReport = () => {
    downloadReportAsExcelFile(
      table.getPrePaginationRowModel().rows,
      table.getVisibleFlatColumns().map((col) => col.id),
      categoryIdToName,
    );
  };

  // Define table columns
  const ch = createMRTColumnHelper<GetBookingsItem>();

  const columns = [
    ch.accessor("id", {
      header: "#",
      Cell: ({ cell, renderedCellValue }) => (
        <AnchorLink
          to={
            museumId
              ? "/museum/$museumId/bookings/$bookingId"
              : "/admin/bookings/$bookingId"
          }
          params={{
            bookingId: cell.getValue().toString(),
            museumId: museumId,
          }}
        >
          {renderedCellValue}
        </AnchorLink>
      ),
    }),
    ch.accessor("arrival", {
      id: "arrival",
      header: t`Ankomst`,
      Cell: ({ cell }) => (
        <Text size="sm">
          {i18n.date(cell.getValue(), {
            dateStyle: "short",
            timeStyle: "short",
          })}
        </Text>
      ),
      sortingFn: (rowA, rowB, _columnId) => {
        const a = new Date(rowA.original.arrival);
        const b = new Date(rowB.original.arrival);

        return a === b ? 0 : a > b ? 1 : -1;
      },
      // filterVariant: "date-range",
      filterFn: (row, _columnId, filterValue) => {
        const [start, end] = filterValue as [
          Date | undefined | "",
          Date | undefined | "",
        ];

        // not set - Date
        if (!start && end) {
          return dayjs(row.original.arrival).isSameOrBefore(end, "day");
        }

        // Date - not set
        if (start && !end) {
          return dayjs(row.original.arrival).isSameOrAfter(start, "day");
        }

        // Date - Date
        if (start && end) {
          return dayjs(row.original.arrival).isBetween(start, end, "day", "[]");
        }

        return true;
      },
    }),
    ch.accessor(
      (row) =>
        i18n.date(row.departure, {
          dateStyle: "short",
          timeStyle: "short",
        }),
      {
        id: "departure",
        header: t`Avreise`,
      },
    ),
    ch.accessor("location.name", {
      header: t`Lokasjon`,
      filterVariant: "select",
    }),
    ch.accessor("department.name", {
      header: t`Avdeling`,
      filterVariant: "select",
    }),
    ch.accessor("group.guests.booked", {
      header: t`Antall forventet`,
      Cell: ({ cell, row }) => (
        <Text size="sm">
          {cell.getValue()} + {row.original.group?.travelLeader.booked}
        </Text>
      ),
    }),
    ch.accessor("group.travelLeader.booked", {
      header: t`Forventet reiseleder/sjåfør`,
    }),
    ch.accessor(
      (row) =>
        (row.group?.arrivals?.sum ?? 0) - (row.group?.arrivals?.staff ?? 0),
      {
        id: "arrivedTotal",
        header: t`Antall ankommet`,
        Cell: ({ cell, row }) => (
          <Text size="sm">
            {cell.getValue()} + {row.original.group?.arrivals?.staff ?? 0}
          </Text>
        ),
      },
    ),
    ch.accessor("group.arrivals.adults", {
      header: t`Ankommet voksne`,
    }),
    ch.accessor("group.arrivals.staff", {
      header: t`Ankommet reiseleder/sjåfør`,
    }),
    ch.accessor("group.arrivals.childrenFree", {
      header: t`Ankommet barn (gratis)`,
    }),
    ch.accessor("group.arrivals.childrenPaid", {
      header: t`Ankommet barn (betalende)`,
    }),
    ch.accessor("group.name", {
      header: t`Gruppe`,
    }),
    ch.accessor("group.contactPerson", {
      header: t`Kontaktperson`,
    }),
    ch.accessor("group.phone", {
      header: t`Kontaktperson mobil`,
    }),
    ch.accessor(
      (row) =>
        row.group?.nationalities
          ?.map((code) => countryMap.get(code) ?? code)
          .join(", ") ?? "-",
      {
        id: "nationalities",
        header: t`Nasjonalitet`,
      },
    ),
    ch.accessor(
      (row) =>
        row.group?.languages
          ?.map((code) => languageMap.get(code) ?? code)
          .join(", ") ?? "-",
      {
        id: "languages",
        header: t`Språk`,
      },
    ),
    ch.accessor("customer.id", {
      header: t`Kunde ID`,
    }),
    ch.accessor("customer.name", { header: t`Kunde`, filterVariant: "select" }),
    ch.accessor(
      (row) =>
        row.visitCategory
          ? i18n.t(bookingVisitCategoryLabels[row.visitCategory]) ||
            row.visitCategory
          : "-",
      {
        id: "visitCategory",
        header: t`Type`,
      },
    ),
    ch.accessor(
      (row) =>
        row.status
          ? i18n.t(bookingStatusLabels[row.status]) || row.status
          : "-",
      {
        id: "status",
        header: t`Status`,
      },
    ),
    ch.accessor(
      (row) =>
        row.paymentCategory
          ? i18n.t(bookingPaymentFormLabels[row.paymentCategory]) ||
            row.paymentCategory
          : "-",
      {
        id: "paymentCategory",
        header: t`Betalingsform`,
      },
    ),
    ch.accessor(
      (row) =>
        row.paymentCategoryDrinks
          ? i18n.t(bookingPaymentFormDrinksLabels[row.paymentCategoryDrinks]) ||
            row.paymentCategoryDrinks
          : "-",
      {
        id: "paymentCategoryDrinks",
        header: t`Betalingsform drikke`,
      },
    ),
    ch.accessor(
      (row) =>
        row.paymentStatus
          ? i18n.t(bookingPaymentStatusLabels[row.paymentStatus]) ||
            row.paymentStatus
          : "-",
      {
        id: "paymentStatus",
        header: t`Betalingsstatus`,
      },
    ),
    ch.accessor(
      (row) =>
        (row.program
          ?.map((p) => p.productCategoryID)
          ?.filter(Boolean) as string[]) ?? [],
      {
        id: "productCategories",
        header: t`Produktkategorier`,
        filterFn: (row, _columnId, filterValue) => {
          const categoryIds = row.getValue<string[]>(_columnId) ?? [];
          const filters = filterValue as string[];

          if (!filters || filters.length === 0) return true;

          // Check if "__NO_CATEGORY__" is in the filter
          const hasNoCategoryFilter = filters.includes("__NO_CATEGORY__");
          const otherFilters = filters.filter((f) => f !== "__NO_CATEGORY__");

          // If row has no categories
          if (categoryIds.length === 0) {
            return hasNoCategoryFilter;
          }

          // If row has categories, check against other filters
          if (otherFilters.length > 0) {
            return otherFilters.some((filter) => categoryIds.includes(filter));
          }

          // If only "__NO_CATEGORY__" is selected and row has categories, exclude it
          return false;
        },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const ids = Array.isArray(value) ? value.map(String) : [];
          const names = Array.from(
            new Set(ids.map((id) => categoryIdToName[id] ?? id)),
          );
          return names.length ? names.join(", ") : "-";
        },
      },
    ),
    ch.accessor(
      (row) => {
        if (!row.program || row.program.length === 0) return "-";
        const programLines = row.program
          .filter((p) => p.productID === null)
          .map((p) => {
            const start = p.start
              ? i18n.date(p.start, { timeStyle: "short" })
              : "";
            const end = p.end ? i18n.date(p.end, { timeStyle: "short" }) : "";
            const comment = p.comment ?? "";
            return `${start}-${end} ${comment}`.trim();
          });
        return programLines.length > 0 ? programLines.join("\n") : "-";
      },
      {
        id: "program",
        header: t`Program`,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (value === "-") return "-";
          return (
            <Stack gap={4}>
              {value.split("\n").map((line, index) => (
                <Text key={index} size="sm">
                  {line}
                </Text>
              ))}
            </Stack>
          );
        },
      },
    ),
    ch.accessor(
      (row) => {
        if (!row.program || row.program.length === 0) return "-";
        const productLines = row.program
          .filter((p) => p.productID !== null)
          .map((p) => {
            // we don't want to display empty string for productNameNO if we productNameEN
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            const productName = p.productNameNO || p.productNameEN || "";
            const start = p.start
              ? i18n.date(p.start, { timeStyle: "short" })
              : "";
            const end = p.end ? i18n.date(p.end, { timeStyle: "short" }) : "";
            const timeRange = start || end ? `${start}-${end}`.trim() : "";
            const price = p.price ? `${p.price} kr` : "";
            const quantity = p.quantity ? `x${p.quantity}` : "";

            const parts = [productName, timeRange, price, quantity].filter(
              Boolean,
            );
            return parts.join(" ");
          });
        return productLines.length > 0 ? productLines.join("\n") : "-";
      },
      {
        id: "products",
        header: t`Produkter`,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (value === "-") return "-";
          return (
            <Stack gap={4}>
              {value.split("\n").map((line, index) => (
                <Text key={index} size="sm">
                  {line}
                </Text>
              ))}
            </Stack>
          );
        },
      },
    ),
    ch.accessor(
      (row) => {
        if (row.sentToPowerOfficeDate) {
          return i18n.date(row.sentToPowerOfficeDate, {
            dateStyle: "short",
            timeStyle: "short",
          });
        }

        return "-";
      },
      {
        id: "sentToPowerOfficeDate",
        header: t`Sendt til PowerOffice`,
      },
    ),
    ch.accessor(
      (row) =>
        row.guide?.firstName
          ? `${row.guide?.firstName} ${row.guide?.lastName ?? ""}`
          : "-",
      {
        id: "guide",
        header: t`Guide`,
      },
    ),
    ch.accessor("externalInformation", {
      header: t`Ekstern informasjon`,
    }),
    ch.accessor("internalInformation", {
      header: t`Intern informasjon`,
    }),
    ch.accessor(
      (row) =>
        i18n.date(row.createdAt, {
          dateStyle: "short",
          timeStyle: "short",
        }),
      {
        id: "createdAt",
        header: t`Opprettet`,
      },
    ),
    ch.accessor(
      (row) =>
        row.createdBy
          ? `${row.createdBy.first_name ?? ""} ${row.createdBy.last_name ?? ""}`.trim()
          : "-",
      {
        id: "createdBy",
        header: t`Opprettet av`,
      },
    ),
    ch.accessor(
      (row) =>
        i18n.date(row.updatedAt, {
          dateStyle: "short",
          timeStyle: "short",
        }),
      {
        id: "updatedAt",
        header: t`Oppdatert`,
      },
    ),
    ch.accessor(
      (row) =>
        row.updatedBy
          ? `${row.updatedBy.first_name ?? ""} ${row.updatedBy.last_name ?? ""}`.trim()
          : "-",
      {
        id: "updatedBy",
        header: t`Oppdatert av`,
      },
    ),
  ];

  const defaultMRTOptions = getDefaultMRTOptions<GetBookingsItem>();

  const table = useMantineReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: useMemo(() => bookingsData?.data ?? [], [bookingsData?.data]),
    initialState: {
      ...defaultMRTOptions.initialState,
      density: "xs",
      sorting: [{ id: "arrival", desc: true }],
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    state: {
      isLoading,
      columnVisibility,
      columnFilters,
    },
    enableTopToolbar: false,
    enableSorting: true,
    enableColumnActions: false,
    enableColumnFilters: false,
  });

  const getVisibleSelectionValues = () => {
    return table.getIsAllColumnsVisible()
      ? []
      : table
          .getAllColumns()
          .filter((c) => c.getIsVisible())
          .map((c) => c.id);
  };

  return (
    <Stack gap="md">
      <Paper withBorder p="xs" mt="sm">
        <Stack gap="lg">
          <Text size="sm" fw={400}>
            <Trans>Instillinger</Trans>
          </Text>

          {/* Time Period Select */}
          <Stack gap="xs">
            <Group>
              <Select
                label={t`Tidsrom`}
                placeholder={t`Velg tidsrom`}
                value={getTimePeriodFromFilter()}
                onChange={(value) =>
                  handleTimePeriodChange(value as TimePeriod)
                }
                data={[
                  { value: "today", label: t`I dag` },
                  { value: "thisWeek", label: t`Denne uken` },
                  { value: "thisMonth", label: t`Denne måneden` },
                  { value: "custom", label: t`Egendefinert` },
                ]}
                searchable
                size="sm"
                allowDeselect={false}
              />

              {/* Custom Date Pickers */}
              <Group grow>
                <MaskedDateInput
                  label={t`Fra dato`}
                  placeholder={t`Velg dato`}
                  value={
                    (
                      table.getColumn("arrival")?.getFilterValue() as
                        | [Date | null, Date | null]
                        | undefined
                    )?.[0] ?? null
                  }
                  onChange={(value) =>
                    table
                      .getColumn("arrival")
                      ?.setFilterValue([
                        value ? dayjs(value).startOf("day").toDate() : null,
                        (
                          table.getColumn("arrival")?.getFilterValue() as
                            | [Date | null, Date | null]
                            | undefined
                        )?.[1] ?? null,
                      ])
                  }
                  clearable
                  size="sm"
                />
                <MaskedDateInput
                  label={t`Til dato`}
                  placeholder={t`Velg dato`}
                  value={
                    (
                      table.getColumn("arrival")?.getFilterValue() as
                        | [Date | null, Date | null]
                        | undefined
                    )?.[1] ?? null
                  }
                  onChange={(value) => {
                    table
                      .getColumn("arrival")
                      ?.setFilterValue([
                        (
                          table.getColumn("arrival")?.getFilterValue() as
                            | [Date | null, Date | null]
                            | undefined
                        )?.[0] ?? null,
                        value ? dayjs(value).endOf("day").toDate() : null,
                      ]);
                  }}
                  clearable
                  size="sm"
                />
              </Group>
            </Group>
          </Stack>

          {/* Filter Inputs */}
          <Group align="flex-start" grow>
            <Select
              label={t`Avdeling`}
              placeholder={t`Alle`}
              data={departmentOptions}
              value={
                (table.getColumn("department.name")?.getFilterValue() as
                  | string
                  | undefined) ?? ""
              }
              onChange={(value) => {
                table.getColumn("department.name")?.setFilterValue(value);
              }}
              clearable
              searchable
              size="sm"
            />
            <Select
              label={t`Kunde`}
              placeholder={t`Alle`}
              data={customerOptions}
              value={
                (table.getColumn("customer.name")?.getFilterValue() as
                  | string
                  | undefined) ?? ""
              }
              onChange={(value) => {
                table.getColumn("customer.name")?.setFilterValue(value);
              }}
              clearable
              searchable
              size="sm"
            />

            <MultiSelect
              label={t`Produktkategori`}
              placeholder={t`Alle`}
              data={categoryOptions}
              value={
                (table.getColumn("productCategories")?.getFilterValue() as
                  | string[]
                  | undefined) ?? []
              }
              onChange={(value) => {
                table.getColumn("productCategories")?.setFilterValue(value);
              }}
              clearable
              searchable
              size="sm"
            />

            <MultiSelect
              label={t`Kollonner`}
              placeholder={t`Alle`}
              data={table.getAllColumns().map((column) => ({
                value: column.id,
                label:
                  typeof column.columnDef.header === "string"
                    ? column.columnDef.header
                    : column.id,
              }))}
              value={getVisibleSelectionValues()}
              onChange={(value) => {
                if (value.length === 0) {
                  return table.setColumnVisibility({});
                }
                const nextVisibility: MRT_VisibilityState = {};
                table.getAllColumns().forEach((col) => {
                  nextVisibility[col.id] = value.includes(col.id);
                });
                table.setColumnVisibility(nextVisibility);
              }}
              clearable
              searchable
              size="sm"
            />
          </Group>

          {/* Action Buttons */}
          <Flex justify="space-between" align="center">
            <Text size="sm" c="dimmed">
              <Trans>Antall bookinger:</Trans>{" "}
              {table.getPrePaginationRowModel().rows.length}
            </Text>
            <Group gap="xs">
              <Button
                variant="outline"
                leftSection={<IconTrash size={16} />}
                onClick={handleClearFilters}
                size="sm"
              >
                <Trans>Tøm filter</Trans>
              </Button>

              <Tooltip
                label={
                  table.getPrePaginationRowModel().rows.length === 0
                    ? t`Ingen bookinger å eksportere`
                    : t`Eksportere ${table.getPrePaginationRowModel().rows.length} bookinger til Excel`
                }
              >
                <Button
                  leftSection={<IconDownload size={16} />}
                  onClick={handleGenerateReport}
                  size="sm"
                  disabled={table.getPrePaginationRowModel().rows.length === 0}
                >
                  <Trans>Generer Excel-rapport</Trans>
                </Button>
              </Tooltip>
            </Group>
          </Flex>
        </Stack>
      </Paper>

      <Title order={3}>
        <Trans>Forhåndsvisning</Trans>
      </Title>

      {/* Bookings Table */}
      <MantineReactTable table={table} />
    </Stack>
  );
}
